import React from 'react';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

// project import
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';
import { ThemeMode } from 'config';
import { getRedesignThemeTokens } from 'components/redesign/redesignTheme';

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
  const { themeDirection, mode, presetColor, fontFamily } = useConfig();

  // Base palette from preset
  const baseTheme = useMemo(() => Palette(mode, presetColor), [mode, presetColor]);

  // Align primary brand color with the right-side sidebar blue
  // Sidebar uses #01A8F8; set buttons (primary) to the same family
  const brandPrimary = '#01A8F8';
  const brandPrimaryDark = '#008fd6';
  const brandPrimaryLight = '#33c6ff';

  const paletteWithBrand = useMemo(
    () => ({
      ...baseTheme.palette,
      ...(mode === ThemeMode.DARK
        ? {
            background: {
              default: '#0d1117',
              paper: '#171c24',
            },
            text: {
              primary: '#edf2f7',
              secondary: '#aab4c3',
              disabled: '#697586',
            },
            divider: '#303844',
            action: {
              ...baseTheme.palette.action,
              active: '#aab4c3',
              hover: 'rgba(255,255,255,0.06)',
              selected: 'rgba(1,168,248,0.16)',
              disabled: 'rgba(237,242,247,0.30)',
              disabledBackground: 'rgba(237,242,247,0.08)',
            },
          }
        : {}),
      primary: {
        ...baseTheme.palette.primary,
        main: brandPrimary,
        dark: brandPrimaryDark,
        light: brandPrimaryLight,
        contrastText: '#ffffff',
      },
    }),
    [baseTheme, mode]
  );

  const themeTypography = useMemo(() => Typography(fontFamily), [fontFamily]);

  const themeCustomShadows = useMemo(() => CustomShadows(createTheme({ palette: paletteWithBrand })), [paletteWithBrand]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440,
        },
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
      palette: paletteWithBrand,
      customShadows: themeCustomShadows,
      typography: themeTypography,
    }),
    [themeDirection, paletteWithBrand, themeTypography, themeCustomShadows]
  );

  const themes = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline enableColorScheme />
        <GlobalStyles styles={{ ':root, body': getRedesignThemeTokens(mode === ThemeMode.DARK) }} />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
